import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Loader2, CheckCircle2, QrCode } from 'lucide-react';
import { paymentApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { goToLogin } from '../utils/authRedirect';

export default function TestPayment() {
  const { user, loading: authLoading } = useAuth();
  const [amount, setAmount] = useState('0.01');
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [status, setStatus] = useState('idle'); // idle, pending, success, failed
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      goToLogin('/test-payment');
    }
  }, [user, authLoading]);

  // 轮询订单状态
    useEffect(() => {
      let intervalId;
      if (status === 'pending' && orderNo) {
        intervalId = setInterval(async () => {
          try {
            const res = await paymentApi.checkStatus(orderNo);
            if (res.data.success && (res.data.status === 1 || res.data.data?.status === 1 || res.data.isPaid)) {
              setStatus('success');
              clearInterval(intervalId);
            }
          } catch (err) {
            console.error('Check status error:', err);
          }
        }, 3000);
      }
      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }, [status, orderNo]);

  const handleGenerate = async () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      setError('请输入有效的金额');
      return;
    }
    
    setError('');
    setLoading(true);
    setQrCodeUrl('');
    setOrderNo('');
    setStatus('idle');

    try {
      const res = await paymentApi.createOrder({
        amount: Number(amount),
        payType: 'wechat', // 测试微信支付
        subject: 'DClaw 测试微信支付'
      });

      if (res.data.success) {
        setQrCodeUrl(res.data.qrCodeUrl);
        setOrderNo(res.data.orderNo);
        setStatus('pending');
      } else {
        setError(res.data.error || '生成支付链接失败');
      }
    } catch (err) {
      setError(err.response?.data?.error || '请求失败');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-green-50/50 dark:bg-green-900/20">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <QrCode className="w-6 h-6 text-green-600 dark:text-green-400" />
            微信扫码支付测试
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {/* 金额输入 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              测试支付金额 (元)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">¥</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0.01"
                step="0.01"
                disabled={status === 'pending' || status === 'success'}
                className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}

          {/* 生成按钮 */}
          {status === 'idle' && (
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> 生成中...</>
              ) : (
                '生成支付二维码'
              )}
            </button>
          )}

          {/* 二维码展示区 */}
          {status === 'pending' && qrCodeUrl && (
            <div className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700 animate-in fade-in zoom-in duration-300">
              <div className="bg-white p-3 rounded-xl shadow-sm mb-4">
                <QRCodeSVG value={qrCodeUrl} size={200} />
              </div>
              <p className="text-gray-900 dark:text-white font-medium mb-1">请使用微信扫一扫</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                正在等待微信支付结果...
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-6 text-sm text-green-600 dark:text-green-400 hover:underline"
              >
                重新生成订单
              </button>
            </div>
          )}

          {/* 成功状态 */}
          {status === 'success' && (
            <div className="flex flex-col items-center justify-center p-8 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 animate-in fade-in zoom-in duration-300">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">微信支付成功！</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
                后台已收到微信回调，订单 {orderNo} 已处理完毕。
              </p>
              <button
                onClick={() => {
                  setStatus('idle');
                  setAmount('0.01');
                }}
                className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors"
              >
                继续测试
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}